import { useState } from "react";
import { BenOption, Tab } from "../types/chatbot.types";

const SESSION_KEY = (benId: string) => `chatbot_sessions_${benId}`;

const getSessions = (benId: string): string[] => {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY(benId)) ?? "[]"); } catch { return []; }
};

const saveSessions = (benId: string, sessions: string[]) =>
  localStorage.setItem(SESSION_KEY(benId), JSON.stringify(sessions));

export const addSession = (benId: string, sessionId: string) =>
  saveSessions(benId, [...getSessions(benId), sessionId]);

export const removeSession = (benId: string, sessionId: string) =>
  saveSessions(benId, getSessions(benId).filter((s) => s !== sessionId));

export const createTab = (beneficiaryId: string, benName: string, sessionId: string): Tab => ({
  id: crypto.randomUUID(),
  beneficiaryId,
  benName,
  sessionId,
  messages: [],
  loading: false,
  hasMore: false,
  offset: 0,
});

export const createEmptyTab = (): Tab => ({
  id: crypto.randomUUID(),
  beneficiaryId: "",
  benName: "New Chat",
  sessionId: "",
  messages: [],
  loading: false,
  hasMore: false,
  offset: 0,
});

export const useChatTabs = (allBens: BenOption[]) => {
  const [tabs, setTabs] = useState<Tab[]>(() => {
    const restored = allBens
      .flatMap((ben) => getSessions(ben._id).map((sessionId) => createTab(ben._id, ben.name, sessionId)))
      .slice(0, 3);
    return restored.length > 0 ? restored : [createEmptyTab()];
  });
  const [activeTabId, setActiveTabId] = useState<string>("");

  const updateTab = (tabId: string, patch: Partial<Tab>) =>
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, ...patch } : t)));

  const addNewSession = () => {
    const tab = createEmptyTab();
    setTabs((prev) => [...prev, tab]);
    setActiveTabId(tab.id);
  };

  const assignBenToTab = (
    tabId: string,
    ben: BenOption,
    onAssigned: (tabId: string, tab: Tab) => void
  ) => {
    const sessionId = crypto.randomUUID();
    addSession(ben._id, sessionId);
    const updatedTab: Tab = {
      ...tabs.find((t) => t.id === tabId)!,
      beneficiaryId: ben._id,
      benName: ben.name,
      sessionId,
    };
    setTabs((prev) => prev.map((t) => (t.id === tabId ? updatedTab : t)));
    onAssigned(tabId, updatedTab);
  };

  const closeTab = (tabId: string, onClose: () => void) => {
    if (tabs.length === 1) { onClose(); return; }
    const tab = tabs.find((t) => t.id === tabId);
    if (tab?.beneficiaryId && tab?.sessionId) removeSession(tab.beneficiaryId, tab.sessionId);
    const idx = tabs.findIndex((t) => t.id === tabId);
    const newTabs = tabs.filter((t) => t.id !== tabId);
    setTabs(newTabs);
    if (activeTabId === tabId) setActiveTabId(newTabs[Math.max(0, idx - 1)].id);
  };

  return { tabs, setTabs, activeTabId, setActiveTabId, updateTab, addNewSession, assignBenToTab, closeTab };
};
